import React, { useState, useMemo, useEffect } from "react";
import swal from "sweetalert";
import {
  Row,
  Col,
  Card,
  Button,
  Badge,
  Modal,
  Form,
  InputGroup,
  ProgressBar,
  Table,
  Pagination,
} from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTasks,
  faProjectDiagram,
  faPlus,
  faCalendarAlt,
  faClock,
  faSearch,
  faFilter,
  faEye,
  faCheckCircle,
  faExclamationTriangle,
  faCommentAlt,
  faStickyNote,
  faPaperPlane,
  faTimes,
  faTag,
  faUser,
  faFolderOpen,
  faHourglassHalf,
  faSyncAlt,
  faFlag,
  faBuilding,
  faChartPie,
  faFileCsv,
  faUserCheck,
  faSlidersH,
  faHistory,
  faEdit,
  faColumns,
  faListUl,
  faLock,
  faPause,
} from "@fortawesome/free-solid-svg-icons";
import {
  useTaskManager,
  formatDisplayDate,
} from "./TaskManagementContext";

export const ManagerTaskManagement = () => {
  const {
    projects,
    tasks,
    employees,
    isFetchingEmployees,
    fetchEmployees,
    managerStats,
    createTask,
    addWorkNote,
    createProject,
    updateProject,
    fetchProjects,
    isFetchingProjects,
    fetchTasks,
    isFetchingTasks,
  } = useTaskManager();

  // Active View Tab inside Manager Task Hub: 'DASHBOARD_TASKS' | 'PROJECTS_VIEW'
  const [activeSubTab, setActiveSubTab] = useState("DASHBOARD_TASKS");
  const [viewMode, setViewMode] = useState("LIST"); // 'LIST' (Table View) | 'KANBAN'

  // Filters & Search State
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProjectFilter, setSelectedProjectFilter] = useState("ALL");
  const [selectedEmployeeFilter, setSelectedEmployeeFilter] = useState("ALL");
  const [selectedStatusFilter, setSelectedStatusFilter] = useState("ALL");
  const [selectedPriorityFilter, setSelectedPriorityFilter] = useState("ALL");
  const [selectedOriginFilter, setSelectedOriginFilter] = useState("ALL"); // 'ALL' | 'MANAGER' | 'EMPLOYEE'
  const [dateFilterType, setDateFilterType] = useState("ALL"); // 'ALL' | 'THIS_WEEK' | 'THIS_MONTH' | 'OVERDUE'

  // Modals
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  // Assign Task Form State
  const [assignProjectId, setAssignProjectId] = useState(projects[0] ? projects[0].id : (projects.length === 0 ? "PRJ-GEN" : ""));
  const [assignEmployeeId, setAssignEmployeeId] = useState("");
  const [assignTitle, setAssignTitle] = useState("");
  const [assignDesc, setAssignDesc] = useState("");
  const [assignStartDate, setAssignStartDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [assignDeadline, setAssignDeadline] = useState("");
  const [assignPriority, setAssignPriority] = useState("High");
  const [assignInitialNote, setAssignInitialNote] = useState("");
  const [assignValidated, setAssignValidated] = useState(false);
  const [isSubmittingTask, setIsSubmittingTask] = useState(false);

  // Sync default assignee and project when lists are ready
  useEffect(() => {
    if (employees && employees.length > 0) {
      const exists = employees.some(
        (emp) =>
          String(emp.user_name) === String(assignEmployeeId) ||
          String(emp.userName) === String(assignEmployeeId) ||
          String(emp.id) === String(assignEmployeeId) ||
          String(emp.name) === String(assignEmployeeId)
      );
      if (!assignEmployeeId || !exists) {
        setAssignEmployeeId(employees[0].user_name || employees[0].userName || employees[0].id || employees[0].name);
      }
    }
  }, [employees, assignEmployeeId]);

  // Re-fetch employee user list whenever Assign Task modal is opened
  useEffect(() => {
    if (showAssignModal && fetchEmployees) {
      fetchEmployees();
    }
  }, [showAssignModal, fetchEmployees]);

  useEffect(() => {
    if ((!assignProjectId || assignProjectId === "PRJ-GEN") && projects && projects.length > 0) {
      setAssignProjectId(projects[0].id);
    }
  }, [projects, assignProjectId]);

  // New Project Form State
  const [newPrjName, setNewPrjName] = useState("");
  const [newPrjCode, setNewPrjCode] = useState("");
  const [newPrjDesc, setNewPrjDesc] = useState("");
  const [newPrjStartDate, setNewPrjStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [newPrjEndDate, setNewPrjEndDate] = useState("");
  const [newPrjDept, setNewPrjDept] = useState(() => sessionStorage.getItem("department") || "Software Team");
  const [newPrjManager, setNewPrjManager] = useState(() => [sessionStorage.getItem("firstName"), sessionStorage.getItem("lastName")].filter(Boolean).join(" ") || sessionStorage.getItem("userName") || "");
  const [newPrjPriority, setNewPrjPriority] = useState("High");
  const [projectValidated, setProjectValidated] = useState(false);
  const [isSubmittingProject, setIsSubmittingProject] = useState(false);

  // Edit Project Form State
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [editPrjName, setEditPrjName] = useState("");
  const [editPrjDesc, setEditPrjDesc] = useState("");
  const [editPrjStartDate, setEditPrjStartDate] = useState("");
  const [editPrjEndDate, setEditPrjEndDate] = useState("");
  const [editPrjStatus, setEditPrjStatus] = useState("IN_PROGRESS");
  const [isSubmittingEditProject, setIsSubmittingEditProject] = useState(false);
  const [editProjectValidated, setEditProjectValidated] = useState(false);

  useEffect(() => {
    if (!newPrjManager && employees && employees.length > 0) {
      setNewPrjManager(employees[0].name);
    }
  }, [employees, newPrjManager]);

  // Detail Modal Guidance / Review State
  const [managerReviewNote, setManagerReviewNote] = useState("");
  const [feedbackSuccessAlert, setFeedbackSuccessAlert] = useState(false);

  // Pagination for Task Tracking Table
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Filtered Tasks for Manager Tracking
  const filteredTasks = useMemo(() => {
    return tasks.filter((task) => {
      // Project filter
      if (selectedProjectFilter !== "ALL") {
        const isMatch =
          String(task.projectId) === String(selectedProjectFilter) ||
          String(task.projectName).toLowerCase() === String(selectedProjectFilter).toLowerCase() ||
          (projects.find((p) => String(p.id) === String(selectedProjectFilter))?.name === task.projectName);
        if (!isMatch) return false;
      }

      // Employee filter
      if (selectedEmployeeFilter !== "ALL" && task.assigneeName !== selectedEmployeeFilter && task.assignedTo !== selectedEmployeeFilter && task.assigneeId !== selectedEmployeeFilter) {
        return false;
      }

      // Status filter
      if (selectedStatusFilter !== "ALL") {
        if (selectedStatusFilter === "Overdue") {
          if (!task.isOverdue) return false;
        } else if (task.status !== selectedStatusFilter) {
          return false;
        }
      }

      // Priority filter
      if (selectedPriorityFilter !== "ALL" && task.priority !== selectedPriorityFilter) {
        return false;
      }

      // Origin filter (Manager Assigned vs Employee Created)
      if (selectedOriginFilter === "MANAGER" && task.createdByType !== "MANAGER") {
        return false;
      }
      if (selectedOriginFilter === "EMPLOYEE" && task.createdByType !== "EMPLOYEE") {
        return false;
      }

      // Date filter
      if (dateFilterType === "OVERDUE" && !task.isOverdue) {
        return false;
      }

      // Search keyword
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        const matchesTitle = (task.title || "").toLowerCase().includes(query);
        const matchesId = (task.id || "").toLowerCase().includes(query);
        const matchesDesc = (task.description || "").toLowerCase().includes(query);
        const matchesAssignee = (task.assigneeName || "").toLowerCase().includes(query);
        const matchesProject = (task.projectName || "").toLowerCase().includes(query);
        if (!matchesTitle && !matchesId && !matchesDesc && !matchesAssignee && !matchesProject) {
          return false;
        }
      }

      return true;
    });
  }, [
    tasks,
    selectedProjectFilter,
    selectedEmployeeFilter,
    selectedStatusFilter,
    selectedPriorityFilter,
    selectedOriginFilter,
    dateFilterType,
    searchQuery,
    projects,
  ]);

  // Paginated records
  const totalRecords = filteredTasks.length;
  const totalPages = Math.ceil(totalRecords / pageSize) || 1;
  const paginatedTasks = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredTasks.slice(start, start + pageSize);
  }, [filteredTasks, currentPage, pageSize]);

  // Open Details Modal
  const handleOpenDetailModal = (task) => {
    setSelectedTask(task);
    setManagerReviewNote("");
    setFeedbackSuccessAlert(false);
    setShowDetailModal(true);
  };

    // Assign Task Submit (POST /tasks/createTask)
  const handleAssignTaskSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!assignTitle.trim() || !assignProjectId || !assignEmployeeId) {
      setAssignValidated(true);
      return;
    }

    const matchedEmp = (employees || []).find(
      (emp) =>
        String(emp.user_name) === String(assignEmployeeId) ||
        String(emp.userName) === String(assignEmployeeId) ||
        String(emp.id) === String(assignEmployeeId) ||
        String(emp.name) === String(assignEmployeeId)
    );

    // Backend payload requires username format: e.g. "sebestin.saldana"
    const chosenUserName =
      matchedEmp?.user_name ||
      matchedEmp?.userName ||
      String(assignEmployeeId).toLowerCase().replace(/\s+/g, ".");

    // UI display requires Title Case format: e.g. "Sebestin Saldana"
    const chosenDisplayName =
      matchedEmp?.name ||
      String(chosenUserName)
        .split(/[._\-\s]+/)
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
        .join(" ");

    const managerName = [sessionStorage.getItem("firstName"), sessionStorage.getItem("lastName")].filter(Boolean).join(" ") || sessionStorage.getItem("userName") || "Manager";
    const managerId = sessionStorage.getItem("pkUserId") || sessionStorage.getItem("userId") || "";

    const matchedP = (projects || []).find(
      (p) => String(p.id) === String(assignProjectId) || String(p.rawId) === String(assignProjectId)
    );
    const chosenProjectName = matchedP?.name || matchedP?.projectName || "General Project";

    setIsSubmittingTask(true);
    try {
      const result = await createTask({
        projectId: assignProjectId,
        projectName: chosenProjectName,
        title: assignTitle.trim(),
        taskName: assignTitle.trim(),
        description: assignDesc.trim(),
        assigneeId: chosenDisplayName,
        assigneeName: chosenDisplayName,
        assignedTo: chosenUserName, // SEND USERNAME IN PAYLOAD: "sebestin.saldana"
        assigneeUserName: chosenUserName, // "sebestin.saldana"
        createdByType: "MANAGER",
        createdById: managerId,
        createdByName: managerName,
        startDate: assignStartDate || new Date().toISOString().split("T")[0],
        deadline: assignDeadline,
        endDate: assignDeadline,
        dueDate: assignDeadline,
        priority: assignPriority,
        status: "Pending",
        progress: 0,
        initialNote: assignInitialNote.trim(),
      });

      swal("Success", result?.message || "Task created successfully.", "success");

      // Reset Form
      setAssignTitle("");
      setAssignDesc("");
      setAssignStartDate(new Date().toISOString().split("T")[0]);
      setAssignDeadline("");
      setAssignPriority("High");
      setAssignInitialNote("");
      setAssignValidated(false);
      setShowAssignModal(false);
    } catch (err) {
      console.error("Assign task error:", err);
      swal("Error", err?.response?.data?.message || err?.message || "Failed to create task", "error");
    } finally {
      setIsSubmittingTask(false);
    }
  };

  // Create Project Submit
  const handleCreateProjectSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!newPrjName || !newPrjName.trim()) {
      setProjectValidated(true);
      swal("Required", "Please enter Project Name", "warning");
      return;
    }
    if (!newPrjStartDate) {
      setProjectValidated(true);
      swal("Required", "Please select Start Date", "warning");
      return;
    }

    console.log("--> [ManagerTaskManagement] Submitting new project:", newPrjName);
    setIsSubmittingProject(true);
    try {
      const result = await createProject({
        name: newPrjName.trim(),
        projectName: newPrjName.trim(),
        code: newPrjCode.trim() || undefined,
        description: newPrjDesc.trim(),
        startDate: newPrjStartDate,
        endDate: newPrjEndDate,
        department: newPrjDept,
        manager: newPrjManager,
        role: "Manager",
        priority: newPrjPriority,
        progress: 0,
      });

      swal("Success", result?.message || "Project added successfully.", "success");

      setNewPrjName("");
      setNewPrjCode("");
      setNewPrjDesc("");
      setNewPrjStartDate(new Date().toISOString().split("T")[0]);
      setNewPrjEndDate("");
      setProjectValidated(false);
      setShowCreateProjectModal(false);
    } catch (err) {
      console.error("Error creating project:", err);
      swal("Error", err?.response?.data?.message || err?.message || "Failed to create project", "error");
    } finally {
      setIsSubmittingProject(false);
    }
  };

  // Open Edit Project Modal with current project attributes
  const handleOpenEditProjectModal = (project) => {
    setEditingProject(project);
    setEditPrjName(project.name || project.projectName || "");
    setEditPrjDesc(project.description || "");
    setEditPrjStartDate(project.startDate ? project.startDate.split("T")[0] : "");
    setEditPrjEndDate(project.endDate ? project.endDate.split("T")[0] : "");
    const rawSt = (project.rawStatus || project.status || "IN_PROGRESS").toString().toUpperCase().replace(/\s+/g, "_");
    setEditPrjStatus(
      rawSt === "IN_PROGRESS" || rawSt === "INPROGRESS"
        ? "IN_PROGRESS"
        : rawSt === "COMPLETED"
        ? "COMPLETED"
        : rawSt === "PENDING"
        ? "PENDING"
        : "IN_PROGRESS"
    );
    setEditProjectValidated(false);
    setShowEditProjectModal(true);
  };

  // Submit Update Project (PUT /projects/updateProject/:id?pkUserId=...&role=MANAGER)
  const handleUpdateProjectSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!editPrjName || !editPrjName.trim()) {
      setEditProjectValidated(true);
      swal("Required", "Please enter Project Name", "warning");
      return;
    }

    if (!editingProject) return;

    const projectId = editingProject.rawId || editingProject.id;
    console.log("--> [ManagerTaskManagement] Updating project id:", projectId, "name:", editPrjName);
    setIsSubmittingEditProject(true);

    try {
      const result = await updateProject(projectId, {
        projectName: editPrjName.trim(),
        description: editPrjDesc.trim(),
        startDate: editPrjStartDate,
        endDate: editPrjEndDate,
        status: editPrjStatus,
      });

      swal("Success", result?.message || "Project updated successfully.", "success");
      setShowEditProjectModal(false);
      setEditingProject(null);
    } catch (err) {
      console.error("Error updating project:", err);
      swal("Error", err?.response?.data?.message || err?.message || "Failed to update project", "error");
    } finally {
      setIsSubmittingEditProject(false);
    }
  };

  // Manager adds guidance / review note without altering employee status
  const handleManagerSaveFeedback = () => {
    if (!selectedTask || !managerReviewNote.trim()) return;

    addWorkNote(selectedTask.id, {
      text: `[Manager Feedback] ${managerReviewNote.trim()}`,
      author: "Project Manager",
      authorRole: "Manager / Reviewer",
      progress: selectedTask.progress,
      status: selectedTask.status,
    });

    setSelectedTask((prev) => ({
      ...prev,
      workNotes: [
        ...(prev.workNotes || []),
        {
          id: `NOTE-${Date.now()}`,
          author: "Project Manager",
          authorRole: "Manager / Reviewer",
          timestamp: new Date().toISOString(),
          text: `[Manager Feedback] ${managerReviewNote.trim()}`,
          progressSnapshot: selectedTask.progress,
          statusSnapshot: selectedTask.status,
        },
      ],
    }));

    setManagerReviewNote("");
    setFeedbackSuccessAlert(true);
    setTimeout(() => setFeedbackSuccessAlert(false), 3000);
  };

  // Export Tasks to CSV
  const handleExportTasksCSV = () => {
    if (!filteredTasks || filteredTasks.length === 0) {
      alert("No tasks available to export for the selected filter.");
      return;
    }

    const headers = [
      "Task ID",
      "Title",
      "Project Name",
      "Assignee",
      "Department",
      "Source (Created By)",
      "Status",
      "Priority",
      "Start Date",
      "End Date",
      "Progress %",
      "Notes Count",
    ];

    const rows = filteredTasks.map((t) => [
      `"${t.id}"`,
      `"${t.title.replace(/"/g, '""')}"`,
      `"${t.projectName}"`,
      `"${t.assigneeName}"`,
      `"${t.assigneeDept || ""}"`,
      `"${t.createdByType === "EMPLOYEE" ? "Employee Created" : "Manager Assigned"}"`,
      `"${t.status}"`,
      `"${t.priority}"`,
      `"${t.startDate || "--"}"`,
      `"${t.deadline || t.endDate || "--"}"`,
      `"${t.progress}%"`,
      `"${t.workNotes ? t.workNotes.length : 0}"`,
    ]);

    const csvContent = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Petropulse_Task_Tracking_Report_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Priority badge renderer (Low, Medium, High)
  const renderPriorityBadge = (priority) => {
    switch (priority) {
      case "Urgent":
      case "High":
        return <span className="badge-priority-high"><FontAwesomeIcon icon={faFlag} className="me-1" />High</span>;
      case "Medium":
        return <span className="badge-priority-medium"><FontAwesomeIcon icon={faFlag} className="me-1" />Medium</span>;
      default:
        return <span className="badge-priority-low"><FontAwesomeIcon icon={faFlag} className="me-1" />Low</span>;
    }
  };

  // Status badge renderer
  const renderStatusBadge = (task) => {
    if (task.isOverdue && task.status !== "Completed" && task.status !== "Cancelled") {
      return (
        <span className="badge-task-overdue">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
          Overdue ({task.status})
        </span>
      );
    }
    switch (task.status) {
      case "Pending":
        return (
          <span className="badge-task-pending">
            <FontAwesomeIcon icon={faClock} className="me-1" />
            Pending
          </span>
        );
      case "In Progress":
        return (
          <span className="badge-task-inprogress">
            <FontAwesomeIcon icon={faSyncAlt} spin className="me-1" />
            In Progress
          </span>
        );
      case "Completed":
        return (
          <span className="badge-task-completed">
            <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
            Completed
          </span>
        );
      case "On Hold":
        return (
          <span className="badge-task-onhold">
            <FontAwesomeIcon icon={faPause} className="me-1" />
            On Hold
          </span>
        );
      case "Cancelled":
        return (
          <span className="badge-task-cancelled">
            <FontAwesomeIcon icon={faTimes} className="me-1" />
            Cancelled
          </span>
        );
      default:
        return <Badge bg="secondary">{task.status}</Badge>;
    }
  };

  // Origin badge renderer
  const renderOriginBadge = (task) => {
    if (task.createdByType === "EMPLOYEE") {
      return (
        <span className="badge-origin-employee" title={`Created by employee: ${task.createdByName || task.assigneeName}`}>
          💡 Employee Created
        </span>
      );
    }
    return (
      <span className="badge-origin-manager" title={`Assigned by Manager: ${task.createdByName || "Operations"}`}>
        👔 Manager Assigned
      </span>
    );
  };

  return (
    <div className="manager-task-management-wrapper mt-3">
      {/* MANAGER DASHBOARD METRICS: ONLY PENDING, IN PROGRESS, COMPLETED */}
      <div className="task-dashboard-hero mb-3">
        <Row className="g-2">
          {/* 1. Pending Tasks */}
          <Col xs={12} sm={4}>
            <div
              className={`task-kpi-card card-kpi-pending ${selectedStatusFilter === "Pending" ? "active-kpi" : ""}`}
              onClick={() => setSelectedStatusFilter(selectedStatusFilter === "Pending" ? "ALL" : "Pending")}
              role="button"
              title="Click to filter pending tasks"
            >
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="task-kpi-label">Pending Tasks</div>
                  <div className="task-kpi-sub">Unstarted backlog</div>
                </div>
                <div className="task-kpi-val text-warning mb-0">{managerStats.pending}</div>
              </div>
            </div>
          </Col>

          {/* 2. In Progress Tasks */}
          <Col xs={12} sm={4}>
            <div
              className={`task-kpi-card card-kpi-inprogress ${selectedStatusFilter === "In Progress" ? "active-kpi" : ""}`}
              onClick={() => setSelectedStatusFilter(selectedStatusFilter === "In Progress" ? "ALL" : "In Progress")}
              role="button"
              title="Click to filter in-progress tasks"
            >
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="task-kpi-label">In Progress</div>
                  <div className="task-kpi-sub">Under active development</div>
                </div>
                <div className="task-kpi-val text-info mb-0">{managerStats.inProgress}</div>
              </div>
            </div>
          </Col>

          {/* 3. Completed Tasks */}
          <Col xs={12} sm={4}>
            <div
              className={`task-kpi-card card-kpi-completed ${selectedStatusFilter === "Completed" ? "active-kpi" : ""}`}
              onClick={() => setSelectedStatusFilter(selectedStatusFilter === "Completed" ? "ALL" : "Completed")}
              role="button"
              title="Click to filter completed tasks"
            >
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="task-kpi-label">Completed Tasks</div>
                  <div className="task-kpi-sub">Delivered &amp; verified</div>
                </div>
                <div className="task-kpi-val text-success mb-0">{managerStats.completed}</div>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* UNIFIED VIEW NAVIGATION & ACTION TOOLBAR */}
      <Card className="task-toolbar-card mb-3 border-0 shadow-sm">
        <Card.Body className="p-3">
          {/* Top Line: Navigation Tabs on left, Action Buttons on right */}
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            {/* Left: View Mode Toggle */}
            <div className="d-flex flex-wrap align-items-center gap-2">
              <div className="btn-group btn-group-sm" role="group">
                <Button
                  variant={activeSubTab === "DASHBOARD_TASKS" && viewMode === "KANBAN" ? "primary" : "outline-secondary"}
                  onClick={() => {
                    setActiveSubTab("DASHBOARD_TASKS");
                    setViewMode("KANBAN");
                  }}
                  title="Board View (Cards grouped by status)"
                  className="fw-bold"
                >
                  <FontAwesomeIcon icon={faColumns} className="me-1" />
                  Board
                </Button>
                <Button
                  variant={activeSubTab === "DASHBOARD_TASKS" && viewMode === "LIST" ? "primary" : "outline-secondary"}
                  onClick={() => {
                    setActiveSubTab("DASHBOARD_TASKS");
                    setViewMode("LIST");
                  }}
                  title="Table View"
                  className="fw-bold"
                >
                  <FontAwesomeIcon icon={faListUl} className="me-1" />
                  Table ({tasks.length})
                </Button>
              </div>

              <Button
                variant={activeSubTab === "PROJECTS_VIEW" ? "primary" : "outline-secondary"}
                size="sm"
                className="fw-bold d-flex align-items-center gap-2"
                onClick={() => {
                  setActiveSubTab("PROJECTS_VIEW");
                  if (typeof fetchProjects === "function") fetchProjects();
                }}
              >
                <FontAwesomeIcon icon={faProjectDiagram} />
                <span>Projects ({projects.length})</span>
              </Button>
            </div>

            {/* Right: Actions (Assign Task, New Project, CSV Export) */}
            <div className="d-flex flex-wrap align-items-center gap-2 ms-auto">
              <Button
                variant="outline-secondary"
                size="sm"
                className="d-flex align-items-center gap-1"
                onClick={() => {
                  if (typeof fetchTasks === "function") fetchTasks();
                }}
                disabled={isFetchingTasks}
                title="Refresh tasks from server"
              >
                <FontAwesomeIcon icon={faSyncAlt} spin={isFetchingTasks} className="text-primary" />
                <span>Refresh</span>
              </Button>

              <Button
                variant="outline-secondary"
                size="sm"
                className="d-flex align-items-center gap-1"
                onClick={handleExportTasksCSV}
                title="Export tasks to CSV"
              >
                <FontAwesomeIcon icon={faFileCsv} className="text-success" />
                <span>Export CSV</span>
              </Button>

              <Button
                variant="outline-primary"
                size="sm"
                className="fw-bold d-flex align-items-center gap-1"
                onClick={() => setShowCreateProjectModal(true)}
              >
                <FontAwesomeIcon icon={faPlus} />
                <span>New Project</span>
              </Button>

              <Button
                variant="success"
                size="sm"
                className="fw-bold d-flex align-items-center gap-2 text-white shadow-sm"
                onClick={() => setShowAssignModal(true)}
              >
                <FontAwesomeIcon icon={faPlus} />
                <span>Assign Task</span>
              </Button>
            </div>
          </div>

          {/* Bottom Line: Filters (Only for Task Dashboard) */}
          {activeSubTab === "DASHBOARD_TASKS" && (
            <div className="pt-2 mt-2 border-top">
              <Row className="g-2 align-items-center">
                {/* Search Bar */}
                <Col xs={12} md={4} lg={3}>
                  <InputGroup size="sm">
                    <InputGroup.Text className="bg-white border-end-0">
                      <FontAwesomeIcon icon={faSearch} className="text-muted" />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search title, ID, assignee..."
                      className="border-start-0 ps-0"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="border-start-0"
                        onClick={() => setSearchQuery("")}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </Button>
                    )}
                  </InputGroup>
                </Col>

                {/* Filter by Project */}
                <Col xs={6} sm={4} md={3} lg={2}>
                  <Form.Select
                    size="sm"
                    value={selectedProjectFilter}
                    onChange={(e) => setSelectedProjectFilter(e.target.value)}
                  >
                    <option value="ALL">📁 All Projects</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>

                {/* Filter by Employee */}
                <Col xs={6} sm={4} md={3} lg={2}>
                  <Form.Select
                    size="sm"
                    value={selectedEmployeeFilter}
                    onChange={(e) => setSelectedEmployeeFilter(e.target.value)}
                  >
                    <option value="ALL">👤 All Employees</option>
                    {(employees || []).map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name} {emp.dept ? `(${emp.dept})` : ""}
                      </option>
                    ))}
                  </Form.Select>
                </Col>

                {/* Filter by Status (Only in Table view) */}
                {viewMode === "LIST" && (
                  <Col xs={6} sm={4} md={3} lg={2}>
                    <Form.Select
                      size="sm"
                      value={selectedStatusFilter}
                      onChange={(e) => setSelectedStatusFilter(e.target.value)}
                    >
                      <option value="ALL">Status: All</option>
                      <option value="Pending">Pending</option>
                      <option value="In Progress">In Progress</option>
                      <option value="On Hold">On Hold</option>
                      <option value="Completed">Completed</option>
                      <option value="Cancelled">Cancelled</option>
                      <option value="Overdue">⚠️ Overdue Only</option>
                    </Form.Select>
                  </Col>
                )}

                {/* Filter by Source Origin */}
                <Col xs={6} sm={4} md={3} lg={2}>
                  <Form.Select
                    size="sm"
                    value={selectedOriginFilter}
                    onChange={(e) => setSelectedOriginFilter(e.target.value)}
                  >
                    <option value="ALL">Source: All Tasks</option>
                    <option value="MANAGER">👔 Manager Assigned</option>
                    <option value="EMPLOYEE">💡 Employee Created</option>
                  </Form.Select>
                </Col>

                {/* Reset Filters Link */}
                {(searchQuery ||
                  selectedProjectFilter !== "ALL" ||
                  selectedEmployeeFilter !== "ALL" ||
                  selectedStatusFilter !== "ALL" ||
                  selectedOriginFilter !== "ALL") && (
                    <Col xs="auto" className="ms-auto">
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-danger fw-bold text-decoration-none fs-8"
                        onClick={() => {
                          setSearchQuery("");
                          setSelectedProjectFilter("ALL");
                          setSelectedEmployeeFilter("ALL");
                          setSelectedStatusFilter("ALL");
                          setSelectedOriginFilter("ALL");
                        }}
                      >
                        <FontAwesomeIcon icon={faTimes} className="me-1" />
                        Reset Filters
                      </Button>
                    </Col>
                  )}
              </Row>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* CONDITIONAL SUB-VIEW: 1) TASK TRACKING TABLE OR 2) PROJECT MANAGEMENT CARDS */}
      {activeSubTab === "PROJECTS_VIEW" ? (
        /* PROJECT MANAGEMENT VIEW */
        <div className="manager-projects-section">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
              <FontAwesomeIcon icon={faProjectDiagram} className="text-primary" />
              <span>Active Projects & Schedules</span>
            </h5>
            <div className="d-flex align-items-center gap-2">
              <span className="fs-7 text-muted">
                Showing {projects.length} organizational projects
              </span>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={fetchProjects}
                disabled={isFetchingProjects}
                title="Refresh projects from server"
                className="d-flex align-items-center gap-1"
              >
                <FontAwesomeIcon icon={faSyncAlt} spin={isFetchingProjects} />
                <span className="d-none d-sm-inline">Refresh</span>
              </Button>
            </div>
          </div>

          <Row className="g-3">
            {projects.length === 0 ? (
              <Col xs={12}>
                <Card className="border-0 shadow-sm text-center py-5">
                  <Card.Body>
                    <FontAwesomeIcon icon={faProjectDiagram} className="fa-3x mb-3 text-secondary opacity-50" />
                    <h6 className="fw-bold text-dark">No Projects Found</h6>
                    <p className="text-muted fs-7 mb-3">Create your first organizational project to start assigning and organizing tasks.</p>
                    <Button variant="primary" size="sm" onClick={() => setShowCreateProjectModal(true)}>
                      <FontAwesomeIcon icon={faPlus} className="me-1" /> Create Project
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ) : (
              projects.map((project) => {
                const projectTasks = tasks.filter(
                  (t) =>
                    t.projectId === project.id ||
                    (project.rawId && String(t.projectId) === String(project.rawId)) ||
                    (t.projectName && (t.projectName === project.name || t.projectName === project.projectName))
                );
                const completedTasks = projectTasks.filter((t) => t.status === "Completed");
                const overdueTasks = projectTasks.filter((t) => t.isOverdue);

                return (
                  <Col xs={12} md={6} xl={4} key={project.id}>
                  <Card className="project-overview-card h-100 border-0 shadow-sm">
                    <div className="project-card-top-bar" style={{ backgroundColor: project.color || "#2563eb" }} />
                    <Card.Body className="p-4 d-flex flex-column justify-content-between">
                      <div>
                        {/* Header: Project Code & Status */}
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="badge bg-light text-dark border fs-8 fw-bold">
                            {project.code || project.id}
                          </span>
                          <div className="d-flex align-items-center gap-2">
                            <span className={`badge ${project.status === "In Progress" ? "bg-info text-white" : project.status === "Completed" ? "bg-success text-white" : "bg-warning text-dark"}`}>
                              {project.status}
                            </span>
                           
                          </div>
                        </div>

                        {/* Title & Description */}
                        <h6 className="project-card-title text-dark fw-bold mb-2">
                          {project.name}
                        </h6>
                        <p className="project-card-desc text-muted fs-7 mb-3">
                          {project.description}
                        </p>

                        {/* Project Dates: Start and End Dates */}
                        <div className="project-dates-box p-2 mb-3 rounded bg-light border fs-7">
                          <div className="d-flex justify-content-between mb-1">
                            <span className="text-muted">Start Date:</span>
                            <span className="fw-semibold text-dark">
                              <FontAwesomeIcon icon={faCalendarAlt} className="me-1 text-primary" />
                              {formatDisplayDate(project.startDate)}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">End / Deadline:</span>
                            <span className="fw-semibold text-dark">
                              <FontAwesomeIcon icon={faCalendarAlt} className="me-1 text-danger" />
                              {formatDisplayDate(project.endDate)}
                            </span>
                          </div>
                        </div>

                        {/* Department & Manager */}
                        <div className="d-flex justify-content-between fs-8 text-muted mb-3">
                          <span>
                            <FontAwesomeIcon icon={faBuilding} className="me-1" />
                            {project.department}
                          </span>
                          <span>
                            <FontAwesomeIcon icon={faUser} className="me-1" />
                            {project.manager}
                          </span>
                        </div>
                      </div>

                      <div>
                        {/* Task Count Summary & Overdue Warning */}
                        <div className="d-flex justify-content-between fs-8 fw-bold mb-1">
                          <span>
                            Tasks: {completedTasks.length} / {projectTasks.length} Done
                          </span>
                          {overdueTasks.length > 0 && (
                            <span className="text-danger">
                              ⚠️ {overdueTasks.length} Overdue
                            </span>
                          )}
                        </div>

                        {/* Progress Bar */}
                        <ProgressBar
                          now={project.progress || 0}
                          variant={project.progress === 100 ? "success" : "primary"}
                          style={{ height: "6px" }}
                          className="mb-3"
                        />

                        {/* Project Actions: View Tasks & Edit Project */}
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="flex-grow-1 fw-semibold d-flex align-items-center justify-content-center gap-2"
                            onClick={() => {
                              setSelectedProjectFilter(project.id);
                              setActiveSubTab("DASHBOARD_TASKS");
                            }}
                          >
                            <FontAwesomeIcon icon={faFilter} />
                            <span>View Tasks ({projectTasks.length})</span>
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="fw-semibold d-flex align-items-center justify-content-center gap-1 px-3"
                            title="Edit Project Details"
                            onClick={() => handleOpenEditProjectModal(project)}
                          >
                            <FontAwesomeIcon icon={faEdit} />
                            <span>Edit</span>
                          </Button>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            }))}
          </Row>
        </div>
      ) : (
        /* TASK TRACKING & MANAGEMENT TABLE (ASSIGNED & EMPLOYEE CREATED) */
        <div className="manager-task-tracking-section">
          {viewMode === "KANBAN" ? (
            /* KANBAN BOARD GROUPED BY STATUS: PENDING, IN PROGRESS, COMPLETED */
            <div className="task-kanban-board mb-4">
              <Row className="g-3">
                {/* COLUMN 1: PENDING */}
                <Col xs={12} md={4}>
                  <div className="kanban-column column-pending">
                    <div className="kanban-column-header">
                      <div className="d-flex align-items-center gap-2">
                        <span className="kanban-col-dot dot-pending" />
                        <h6 className="mb-0 fw-bold">Pending</h6>
                      </div>
                      <Badge bg="warning" text="dark" pill>
                        {filteredTasks.filter((t) => t.status === "Pending").length}
                      </Badge>
                    </div>
                    <div className="kanban-cards-container">
                      {filteredTasks.filter((t) => t.status === "Pending").length === 0 ? (
                        <div className="kanban-empty-slot">No pending tasks</div>
                      ) : (
                        filteredTasks
                          .filter((t) => t.status === "Pending")
                          .map((task) => (
                            <div
                              key={task.id}
                              className={`task-kanban-card ${task.isOverdue && task.status !== "Completed" ? "card-is-overdue" : ""}`}
                              onClick={() => handleOpenDetailModal(task)}
                              role="button"
                            >
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="badge bg-light text-dark border fs-8 fw-semibold">{task.id}</span>
                                {renderPriorityBadge(task.priority)}
                              </div>
                              <h6 className="task-card-title mb-1 text-dark">{task.title}</h6>
                              <div className="task-card-project mb-2">
                                📁 <span className="text-primary fw-semibold">{task.projectName}</span>
                              </div>
                              <div className="d-flex justify-content-between align-items-center mb-2 fs-8">
                                <span className="text-dark fw-semibold">
                                  <FontAwesomeIcon icon={faUser} className="me-1 text-muted" />
                                  {task.assigneeName}
                                </span>
                                <span className={task.isOverdue && task.status !== "Completed" ? "text-danger fw-bold" : "text-muted"}>
                                  <FontAwesomeIcon icon={faCalendarAlt} className="me-1 text-danger" />
                                  {formatDisplayDate(task.deadline || task.endDate)}
                                </span>
                              </div>
                              <div className="mb-2">
                                <div className="d-flex justify-content-between fs-8 fw-bold mb-1">
                                  <span>Progress</span>
                                  <span>{task.progress}%</span>
                                </div>
                                <ProgressBar
                                  now={task.progress}
                                  variant={task.progress === 100 ? "success" : task.progress > 50 ? "info" : "warning"}
                                  style={{ height: "5px" }}
                                />
                              </div>
                              <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                                {renderOriginBadge(task)}
                                <span className="fs-8 text-primary fw-bold">
                                  <FontAwesomeIcon icon={faCommentAlt} className="me-1" />
                                  {task.workNotes ? task.workNotes.length : 0} notes
                                </span>
                              </div>
                            </div>
                          ))
                      )}
                    </div>
                  </div>
                </Col>

                {/* COLUMN 2: IN PROGRESS */}
                <Col xs={12} md={4}>
                  <div className="kanban-column column-inprogress">
                    <div className="kanban-column-header">
                      <div className="d-flex align-items-center gap-2">
                        <span className="kanban-col-dot dot-inprogress" />
                        <h6 className="mb-0 fw-bold">In Progress</h6>
                      </div>
                      <Badge bg="info" pill>
                        {filteredTasks.filter((t) => t.status === "In Progress").length}
                      </Badge>
                    </div>
                    <div className="kanban-cards-container">
                      {filteredTasks.filter((t) => t.status === "In Progress").length === 0 ? (
                        <div className="kanban-empty-slot">No tasks in progress</div>
                      ) : (
                        filteredTasks
                          .filter((t) => t.status === "In Progress")
                          .map((task) => (
                            <div
                              key={task.id}
                              className={`task-kanban-card ${task.isOverdue && task.status !== "Completed" ? "card-is-overdue" : ""}`}
                              onClick={() => handleOpenDetailModal(task)}
                              role="button"
                            >
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="badge bg-light text-dark border fs-8 fw-semibold">{task.id}</span>
                                {renderPriorityBadge(task.priority)}
                              </div>
                              <h6 className="task-card-title mb-1 text-dark">{task.title}</h6>
                              <div className="task-card-project mb-2">
                                📁 <span className="text-primary fw-semibold">{task.projectName}</span>
                              </div>
                              <div className="d-flex justify-content-between align-items-center mb-2 fs-8">
                                <span className="text-dark fw-semibold">
                                  <FontAwesomeIcon icon={faUser} className="me-1 text-muted" />
                                  {task.assigneeName}
                                </span>
                                <span className={task.isOverdue && task.status !== "Completed" ? "text-danger fw-bold" : "text-muted"}>
                                  <FontAwesomeIcon icon={faCalendarAlt} className="me-1 text-danger" />
                                  {formatDisplayDate(task.deadline || task.endDate)}
                                </span>
                              </div>
                              <div className="mb-2">
                                <div className="d-flex justify-content-between fs-8 fw-bold mb-1">
                                  <span>Progress</span>
                                  <span>{task.progress}%</span>
                                </div>
                                <ProgressBar
                                  now={task.progress}
                                  variant={task.progress === 100 ? "success" : task.progress > 50 ? "info" : "warning"}
                                  style={{ height: "5px" }}
                                />
                              </div>
                              <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                                {renderOriginBadge(task)}
                                <span className="fs-8 text-primary fw-bold">
                                  <FontAwesomeIcon icon={faCommentAlt} className="me-1" />
                                  {task.workNotes ? task.workNotes.length : 0} notes
                                </span>
                              </div>
                            </div>
                          ))
                      )}
                    </div>
                  </div>
                </Col>

                {/* COLUMN 3: COMPLETED */}
                <Col xs={12} md={4}>
                  <div className="kanban-column column-completed">
                    <div className="kanban-column-header">
                      <div className="d-flex align-items-center gap-2">
                        <span className="kanban-col-dot dot-completed" />
                        <h6 className="mb-0 fw-bold">Completed</h6>
                      </div>
                      <Badge bg="success" pill>
                        {filteredTasks.filter((t) => t.status === "Completed").length}
                      </Badge>
                    </div>
                    <div className="kanban-cards-container">
                      {filteredTasks.filter((t) => t.status === "Completed").length === 0 ? (
                        <div className="kanban-empty-slot">No completed tasks yet</div>
                      ) : (
                        filteredTasks
                          .filter((t) => t.status === "Completed")
                          .map((task) => (
                            <div
                              key={task.id}
                              className={`task-kanban-card ${task.isOverdue && task.status !== "Completed" ? "card-is-overdue" : ""}`}
                              onClick={() => handleOpenDetailModal(task)}
                              role="button"
                            >
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="badge bg-light text-dark border fs-8 fw-semibold">{task.id}</span>
                                {renderPriorityBadge(task.priority)}
                              </div>
                              <h6 className="task-card-title mb-1 text-dark">{task.title}</h6>
                              <div className="task-card-project mb-2">
                                📁 <span className="text-primary fw-semibold">{task.projectName}</span>
                              </div>
                              <div className="d-flex justify-content-between align-items-center mb-2 fs-8">
                                <span className="text-dark fw-semibold">
                                  <FontAwesomeIcon icon={faUser} className="me-1 text-muted" />
                                  {task.assigneeName}
                                </span>
                                <span className="text-success fw-semibold">
                                  <FontAwesomeIcon icon={faCheckCircle} className="me-1 text-success" />
                                  {formatDisplayDate(task.deadline || task.endDate)}
                                </span>
                              </div>
                              <div className="mb-2">
                                <div className="d-flex justify-content-between fs-8 fw-bold mb-1">
                                  <span>Progress</span>
                                  <span>{task.progress}%</span>
                                </div>
                                <ProgressBar
                                  now={task.progress}
                                  variant="success"
                                  style={{ height: "5px" }}
                                />
                              </div>
                              <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                                {renderOriginBadge(task)}
                                <span className="fs-8 text-primary fw-bold">
                                  <FontAwesomeIcon icon={faCommentAlt} className="me-1" />
                                  {task.workNotes ? task.workNotes.length : 0} notes
                                </span>
                              </div>
                            </div>
                          ))
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          ) : (
            /* MAIN TASKS TABLE */
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-0">
                <div className="table-responsive task-table-vertical-wrap">
                  <div className="mobile-table-scroll-hint">
                    <span>Swipe horizontally to view all task columns &rarr;</span>
                  </div>
                  <Table hover className="align-middle mb-0 task-table">
                    <thead className="table-green">
                      <tr>
                        <th>Task ID & Objective</th>
                        <th>Project</th>
                        <th>Assigned Employee</th>
                        <th>Origin / Source</th>
                        <th>Priority</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Progress</th>
                        <th>Status</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isFetchingTasks && paginatedTasks.length === 0 ? (
                        <tr>
                          <td colSpan={10} className="text-center py-5 text-primary">
                            <FontAwesomeIcon icon={faSyncAlt} spin className="fa-2x mb-3" />
                            <p className="mb-0 fw-semibold">Loading manager tasks from server...</p>
                          </td>
                        </tr>
                      ) : paginatedTasks.length === 0 ? (
                        <tr>
                          <td colSpan={10} className="text-center py-5 text-muted">
                            <FontAwesomeIcon icon={faTasks} className="fa-3x mb-3 text-secondary opacity-50" />
                            <p className="mb-0 fw-semibold">
                              {tasks.length === 0
                                ? "No tasks found. Click 'Assign Task' above to assign tasks to team members."
                                : "No tasks matched your filter criteria."}
                            </p>
                          </td>
                        </tr>
                      ) : (
                        paginatedTasks.map((task) => (
                          <tr key={task.id}>
                            <td>
                              <div className="fw-bold text-dark">{task.title}</div>
                              <div className="text-muted fs-7">
                                <span className="badge bg-light text-dark border me-1">{task.id}</span>
                                {task.tags && task.tags.slice(0, 2).join(", ")}
                              </div>
                            </td>
                            <td>
                              <span className="badge bg-primary-soft text-primary fw-semibold">
                                📁 {task.projectName}
                              </span>
                            </td>
                            <td>
                              <div className="fw-semibold text-dark fs-7">
                                <FontAwesomeIcon icon={faUser} className="me-1 text-muted" />
                                {task.assigneeName}
                              </div>
                              <div className="text-muted fs-8">{task.assigneeDept}</div>
                            </td>
                            <td>{renderOriginBadge(task)}</td>
                            <td>{renderPriorityBadge(task.priority)}</td>
                            <td>
                              <div className="fs-7 text-dark fw-semibold">
                                <FontAwesomeIcon icon={faCalendarAlt} className="me-1 text-primary" />
                                {formatDisplayDate(task.startDate)}
                              </div>
                            </td>
                            <td>
                              <div className="fs-7 text-dark fw-semibold">
                                <FontAwesomeIcon icon={faCalendarAlt} className="me-1 text-danger" />
                                {formatDisplayDate(task.deadline || task.endDate)}
                              </div>
                              {task.isOverdue && task.status !== "Completed" && (
                                <span className="text-danger fs-8 fw-bold">⚠️ Overdue</span>
                              )}
                            </td>
                            <td style={{ minWidth: "110px" }}>
                              <div className="d-flex justify-content-between fs-8 fw-semibold mb-1">
                                <span>Progress</span>
                                <span className="text-primary">{task.progress}%</span>
                              </div>
                              <ProgressBar
                                now={task.progress}
                                variant={task.progress === 100 ? "success" : task.progress > 50 ? "info" : "warning"}
                                style={{ height: "6px" }}
                              />
                            </td>
                            <td>
                              {renderStatusBadge(task)}
                            </td>
                            <td className="text-end">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="btn-update-task"
                                onClick={() => handleOpenDetailModal(task)}
                              >
                                <FontAwesomeIcon icon={faEye} className="me-1" />
                                Review & Notes
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>

                {/* PAGINATION FOOTER */}
                <div className="p-3 border-top d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <div className="d-flex align-items-center gap-2">
                    <span className="fs-7 text-muted">
                      Showing {totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1} to{" "}
                      {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords} tasks
                    </span>
                    <div className="d-flex align-items-center gap-1 ms-2">
                      <span className="fs-8 text-muted fw-bold">Rows:</span>
                      <Form.Select
                        size="sm"
                        style={{ width: "70px", height: "30px", fontSize: "0.75rem" }}
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </Form.Select>
                    </div>
                  </div>

                  {totalPages > 1 && (
                    <Pagination size="sm" className="mb-0">
                      <Pagination.Prev
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      />
                      {[...Array(totalPages)].map((_, idx) => (
                        <Pagination.Item
                          key={idx + 1}
                          active={idx + 1 === currentPage}
                          onClick={() => setCurrentPage(idx + 1)}
                        >
                          {idx + 1}
                        </Pagination.Item>
                      ))}
                      <Pagination.Next
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      />
                    </Pagination>
                  )}
                </div>
              </Card.Body>
            </Card>
          )}
        </div>
      )}

      {/* MODAL 1: TASK ASSIGNMENT (CREATE PROJECT-WISE TASK & ASSIGN EMPLOYEE) */}
      <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)} size="lg" centered>
        <Form onSubmit={handleAssignTaskSubmit}>
          <Modal.Header closeButton className="tracker-modal-header bg-dark text-white">
            <Modal.Title className="fs-5 d-flex align-items-center gap-2">
              <FontAwesomeIcon icon={faPlus} className="text-success" />
              <span>Assign Project Task to Employee</span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <div className="alert alert-info border-0 py-2 px-3 fs-7 mb-3 d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div>
                👔 <strong>Task Assignment:</strong> Task will be assigned to employee queue.
              </div>
            
            </div>
            <Row className="g-3">
              {/* Project Selection */}
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold fs-7">Select Project <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    required
                    value={assignProjectId}
                    onChange={(e) => setAssignProjectId(e.target.value)}
                  >
                    {projects.length === 0 ? (
                      <option value="PRJ-GEN">General / Direct Activities (Default)</option>
                    ) : (
                      projects.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} {p.code ? `(${p.code})` : ""}
                        </option>
                      ))
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Assignee Selection */}
              <Col xs={12} md={6}>
                <Form.Group>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Form.Label className="fw-bold fs-7 mb-0">
                      Assign to Employee <span className="text-danger">*</span>
                    </Form.Label>
                    {isFetchingEmployees && (
                      <span className="fs-8 text-primary">
                        <FontAwesomeIcon icon={faSyncAlt} spin className="me-1" />
                        Loading...
                      </span>
                    )}
                  </div>
                  <Form.Select
                    required
                    value={assignEmployeeId}
                    onChange={(e) => setAssignEmployeeId(e.target.value)}
                    disabled={isFetchingEmployees && (!employees || employees.length === 0)}
                  >
                    {(!employees || employees.length === 0) ? (
                      <option value="">{isFetchingEmployees ? "Loading employees from server..." : "No employees available"}</option>
                    ) : (
                      employees.map((emp) => {
                        const empVal = emp.user_name || emp.userName || emp.id || emp.name;
                        return (
                          <option key={empVal} value={empVal}>
                            {emp.name}
                          </option>
                        );
                      })
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Priority */}
              <Col xs={12} md={4}>
                <Form.Group>
                  <Form.Label className="fw-bold fs-7">Priority Level</Form.Label>
                  <Form.Select
                    value={assignPriority}
                    onChange={(e) => setAssignPriority(e.target.value)}
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Start Date */}
              <Col xs={12} md={4}>
                <Form.Group>
                  <Form.Label className="fw-bold fs-7">Start Date <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    required
                    value={assignStartDate}
                    onChange={(e) => setAssignStartDate(e.target.value)}
                  />
                </Form.Group>
              </Col>

              {/* End Date */}
              <Col xs={12} md={4}>
                <Form.Group>
                  <Form.Label className="fw-bold fs-7">End Date <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    required
                    value={assignDeadline}
                    onChange={(e) => setAssignDeadline(e.target.value)}
                  />
                </Form.Group>
              </Col>

              {/* Task Title */}
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-bold fs-7">Task Title & Deliverable <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    required
                    placeholder="e.g. Develop high-precision sensor proving and calibration routine..."
                    value={assignTitle}
                    onChange={(e) => setAssignTitle(e.target.value)}
                    isInvalid={assignValidated && !assignTitle.trim()}
                  />
                </Form.Group>
              </Col>

              {/* Detailed Scope */}
              {/* <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-bold fs-7">Scope & Technical Specifications</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Detailed instructions, acceptance test criteria, references..."
                    value={assignDesc}
                    onChange={(e) => setAssignDesc(e.target.value)}
                  />
                </Form.Group>
              </Col> */}

              {/* Initial Instructions Note */}
              {/* <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-bold fs-7">Manager Initial Note (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. High priority for Q4 release. Coordinate with firmware lead on API spec."
                    value={assignInitialNote}
                    onChange={(e) => setAssignInitialNote(e.target.value)}
                  />
                </Form.Group>
              </Col> */}
            </Row>
          </Modal.Body>
          <Modal.Footer className="bg-light">
            <Button variant="secondary" size="sm" onClick={() => setShowAssignModal(false)} disabled={isSubmittingTask}>
              Cancel
            </Button>
            <Button variant="success" size="sm" type="submit" className="fw-bold text-white px-4" disabled={isSubmittingTask}>
              {isSubmittingTask ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Assigning Task...
                </>
              ) : (
                "Submit"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* MODAL 2: CREATE PROJECT */}
      <Modal show={showCreateProjectModal} onHide={() => setShowCreateProjectModal(false)} size="lg" centered>
        <Form onSubmit={handleCreateProjectSubmit}>
          <Modal.Header closeButton className="tracker-modal-header bg-dark text-white">
            <Modal.Title className="fs-5 d-flex align-items-center gap-2">
              <FontAwesomeIcon icon={faProjectDiagram}  />
              <span>Create New Project</span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <Row className="g-3">
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold fs-7">Project Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    required
                    placeholder="e.g. NextGen Terminal Automation 2027"
                    value={newPrjName}
                    onChange={(e) => setNewPrjName(e.target.value)}
                    isInvalid={projectValidated && !newPrjName.trim()}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold fs-7">Department</Form.Label>
                  <Form.Select
                    value={newPrjDept}
                    onChange={(e) => setNewPrjDept(e.target.value)}
                  >
                    <option value="Software Team">Software Team</option>
                    <option value="Firmware Team">Firmware Team</option>
                    <option value="Electronic Team">Electronic Team</option>
                    <option value="Hardware Team">Hardware Team</option>
                    <option value="Field Service">Field Service</option>
                    <option value="Dispatch & Logistics">Dispatch & Logistics</option>
                    <option value="Production & Assembly">Production & Assembly</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              {/* <Col xs={12} md={4}>
                <Form.Group>
                  <Form.Label className="fw-bold fs-7">Project Code</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. PP-TERM-2027"
                    value={newPrjCode}
                    onChange={(e) => setNewPrjCode(e.target.value)}
                  />
                </Form.Group>
              </Col> */}
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-bold fs-7">Project Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Objectives, deliverables, and operational requirements..."
                    value={newPrjDesc}
                    onChange={(e) => setNewPrjDesc(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold fs-7">Start Date <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    required
                    value={newPrjStartDate}
                    onChange={(e) => setNewPrjStartDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold fs-7">Target End Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={newPrjEndDate}
                    onChange={(e) => setNewPrjEndDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
              
              {/* <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold fs-7">Project Lead / Manager</Form.Label>
                  <Form.Select
                    value={newPrjManager}
                    onChange={(e) => setNewPrjManager(e.target.value)}
                  >
                    {(employees || []).map((emp) => (
                      <option key={emp.id} value={emp.name}>
                        {emp.name} {emp.dept ? `(${emp.dept})` : ""}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col> */}
            </Row>
          </Modal.Body>
          <Modal.Footer className="bg-light">
            <Button variant="secondary" size="sm" onClick={() => setShowCreateProjectModal(false)} disabled={isSubmittingProject}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="button"
              className="fw-bold px-4"
              disabled={isSubmittingProject}
              onClick={handleCreateProjectSubmit}
            >
              {isSubmittingProject ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Creating Project...
                </>
              ) : (
                "Submit"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* MODAL 2.5: EDIT / UPDATE PROJECT */}
      <Modal show={showEditProjectModal} onHide={() => setShowEditProjectModal(false)} size="lg" centered>
        <Form onSubmit={handleUpdateProjectSubmit}>
          <Modal.Header closeButton className="tracker-modal-header">
            <div className="d-flex align-items-center gap-3">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: "42px", height: "42px", background: "rgba(59, 130, 246, 0.2)", color: "#60a5fa" }}
              >
                <FontAwesomeIcon icon={faEdit} className="fs-5" />
              </div>
              <div>
                <Modal.Title className="fs-5 fw-bold text-white mb-0">Update Project Details</Modal.Title>
                <small className="text-light opacity-75">
                  Update schedule, project information, and execution status
                </small>
              </div>
            </div>
          </Modal.Header>
          <Modal.Body className="p-4">
            {/* Project Context Pill Banner */}
            <div className="p-3 mb-3 rounded-3 border bg-light d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div className="d-flex align-items-center gap-2">
                <span className="badge bg-primary px-2.5 py-1.5 fs-7 fw-bold">
                  {editingProject?.code || `PRJ-${editingProject?.id || editingProject?.rawId || "01"}`}
                </span>
                <span className="fw-bold text-dark fs-6">
                  {editingProject?.name || editingProject?.projectName || "Selected Project"}
                </span>
              </div>
              <div className="d-flex align-items-center gap-3 fs-7 text-muted">
                {editingProject?.department && (
                  <span>
                    <FontAwesomeIcon icon={faBuilding} className="me-1 text-primary" />
                    {editingProject.department}
                  </span>
                )}
                {editingProject?.manager && (
                  <span>
                    <FontAwesomeIcon icon={faUser} className="me-1 text-secondary" />
                    {editingProject.manager}
                  </span>
                )}
              </div>
            </div>

            {/* Interactive Status Selector */}
            <div className="mb-3">
              <Form.Label className="fw-bold fs-7 text-dark mb-2">
                Project Status <span className="text-danger">*</span>
              </Form.Label>
              <Row className="g-2">
                {[
                  {
                    value: "IN_PROGRESS",
                    label: "In Progress",
                    desc: "Active execution",
                    icon: faHourglassHalf,
                    activeColor: "#2563eb",
                    bgActive: "#eff6ff",
                    borderActive: "#3b82f6",
                  },
                  {
                    value: "PENDING",
                    label: "Pending",
                    desc: "Queued / on hold",
                    icon: faClock,
                    activeColor: "#d97706",
                    bgActive: "#fffbeb",
                    borderActive: "#f59e0b",
                  },
                  {
                    value: "COMPLETED",
                    label: "Completed",
                    desc: "Finalized & delivered",
                    icon: faCheckCircle,
                    activeColor: "#059669",
                    bgActive: "#ecfdf5",
                    borderActive: "#10b981",
                  },
                ].map((st) => {
                  const isSelected = editPrjStatus === st.value;
                  return (
                    <Col xs={12} sm={4} key={st.value}>
                      <div
                        onClick={() => setEditPrjStatus(st.value)}
                        role="button"
                        tabIndex={0}
                        className="p-2.5 rounded-3 border text-center transition-all"
                        style={{
                          cursor: "pointer",
                          borderColor: isSelected ? st.borderActive : "#e2e8f0",
                          backgroundColor: isSelected ? st.bgActive : "#ffffff",
                          boxShadow: isSelected ? "0 2px 8px rgba(0,0,0,0.06)" : "none",
                          transition: "all 0.2s ease",
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-center gap-1.5 mb-1" style={{ color: isSelected ? st.activeColor : "#64748b" }}>
                          <FontAwesomeIcon icon={st.icon} />
                          <span className="fw-bold fs-7">{st.label}</span>
                        </div>
                        <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                          {st.desc}
                        </div>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </div>

            {/* Project Name */}
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold fs-7 text-dark">
                Project Name <span className="text-danger">*</span>
              </Form.Label>
              <InputGroup>
                <InputGroup.Text className="bg-light border-end-0 text-muted">
                  <FontAwesomeIcon icon={faFolderOpen} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  required
                  className="border-start-0 ps-0"
                  placeholder="e.g. Petropulse Terminal System"
                  value={editPrjName}
                  onChange={(e) => setEditPrjName(e.target.value)}
                  isInvalid={editProjectValidated && !editPrjName.trim()}
                />
              </InputGroup>
              {editProjectValidated && !editPrjName.trim() && (
                <div className="text-danger fs-8 mt-1">Please enter a valid project name</div>
              )}
            </Form.Group>

            {/* Schedule Timeline */}
            <div className="mb-3">
              <div className="fw-bold fs-7 text-dark mb-2 d-flex align-items-center gap-1.5">
                <FontAwesomeIcon icon={faCalendarAlt} className="text-primary" />
                <span>Project Schedule Timeline</span>
              </div>
              <Row className="g-3">
                <Col xs={12} sm={6}>
                  <Form.Group>
                    <Form.Label className="text-muted fs-8 mb-1">Start Date</Form.Label>
                    <InputGroup>
                      <InputGroup.Text className="bg-light border-end-0 text-muted">
                        <FontAwesomeIcon icon={faCalendarAlt} />
                      </InputGroup.Text>
                      <Form.Control
                        type="date"
                        className="border-start-0 ps-0"
                        value={editPrjStartDate}
                        onChange={(e) => setEditPrjStartDate(e.target.value)}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Group>
                    <Form.Label className="text-muted fs-8 mb-1">Target End Date</Form.Label>
                    <InputGroup>
                      <InputGroup.Text className="bg-light border-end-0 text-muted">
                        <FontAwesomeIcon icon={faCalendarAlt} />
                      </InputGroup.Text>
                      <Form.Control
                        type="date"
                        className="border-start-0 ps-0"
                        value={editPrjEndDate}
                        onChange={(e) => setEditPrjEndDate(e.target.value)}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>
              {editPrjStartDate && editPrjEndDate && (
                <div className="mt-2 text-muted fs-8 d-flex align-items-center gap-2">
                  <span>
                    🗓️ Timeline Duration: {Math.max(1, Math.round((new Date(editPrjEndDate) - new Date(editPrjStartDate)) / (1000 * 60 * 60 * 24)))} days
                  </span>
                  {new Date(editPrjEndDate) < new Date(editPrjStartDate) && (
                    <span className="text-danger fw-semibold">
                      ⚠️ Target end date is earlier than start date
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Project Description */}
            <Form.Group className="mb-2">
              <Form.Label className="fw-bold fs-7 text-dark">Project Scope & Deliverables</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                className="rounded-2"
                placeholder="Field system, deliverables, technical requirements..."
                value={editPrjDesc}
                onChange={(e) => setEditPrjDesc(e.target.value)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="bg-light border-top px-4 py-3 d-flex justify-content-between align-items-center">
            <div className="text-muted fs-8 d-none d-sm-block">
              <FontAwesomeIcon icon={faCheckCircle} className="me-1 text-success" />
              Manager authorized updates
            </div>
            <div className="d-flex align-items-center gap-2">
              <Button
                variant="outline-secondary"
                size="sm"
                className="px-3"
                onClick={() => setShowEditProjectModal(false)}
                disabled={isSubmittingEditProject}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                type="button"
                className="fw-bold px-4 d-flex align-items-center gap-2 shadow-sm"
                disabled={isSubmittingEditProject}
                onClick={handleUpdateProjectSubmit}
              >
                {isSubmittingEditProject ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    <span>Updating Project...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    <span>Update Project</span>
                  </>
                )}
              </Button>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* MODAL 3: TASK DETAILS, REVIEWS & WORK NOTES AUDIT */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg" centered>
        {selectedTask && (
          <>
            <Modal.Header closeButton className="border-bottom px-4 py-3 bg-white">
              <div className="w-100 pe-3">
                {/* Task ID & Origin */}
                <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                  <span className="badge bg-light text-secondary border font-monospace fs-7 px-2 py-1">
                    <span className="text-muted fw-normal me-1">Task ID:</span>{selectedTask.id}
                  </span>
                  {renderOriginBadge(selectedTask)}
                </div>

                {/* Project Name */}
                <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                  <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 px-2 py-1 fs-7 fw-semibold d-inline-flex align-items-center gap-1">
                    <FontAwesomeIcon icon={faFolderOpen} className="text-warning" />
                    <span>Project Name: {selectedTask.projectName}</span>
                  </span>
                </div>

                {/* Task Title */}
                <div className="d-flex align-items-baseline gap-2">
                  <span className="text-muted fs-7 fw-bold text-uppercase">Title:</span>
                  <Modal.Title className="fs-5 fw-bold text-dark mb-0 text-break">
                    {selectedTask.title}
                  </Modal.Title>
                </div>
              </div>
            </Modal.Header>
            <Modal.Body className="px-4 py-3">
              {/* Task Description */}
              <div className="p-3 mb-3 bg-light rounded-3 border">
                <div className="fs-8 text-muted fw-bold text-uppercase mb-1 d-flex align-items-center gap-1">
                  <FontAwesomeIcon icon={faStickyNote} className="text-primary small" />
                  <span>Description:</span>
                </div>
                <div className="fs-7 text-dark lh-base" style={{ whiteSpace: "pre-wrap" }}>
                  {selectedTask.description ? (
                    selectedTask.description
                  ) : (
                    <span className="text-muted fst-italic">No description provided for this task.</span>
                  )}
                </div>
              </div>

              {/* Quick Info Bar: Priority, Assignee, Dates */}
              <Row className="g-2 mb-3">
                <Col xs={6} sm={3}>
                  <div className="p-2 border rounded-2 bg-white text-center h-100">
                    <div className="fs-8 text-muted fw-bold text-uppercase">Priority:</div>
                    <div className="mt-1 d-flex justify-content-center">
                      {renderPriorityBadge(selectedTask.priority)}
                    </div>
                  </div>
                </Col>
                <Col xs={6} sm={3}>
                  <div className="p-2 border rounded-2 bg-white text-center h-100">
                    <div className="fs-8 text-muted fw-bold text-uppercase">Assigned To:</div>
                    <div className="fw-semibold text-dark fs-7 text-truncate mt-1" title={selectedTask.assigneeName}>
                      <FontAwesomeIcon icon={faUser} className="me-1 text-primary small" />
                      {selectedTask.assigneeName}
                    </div>
                    {selectedTask.assigneeDept && (
                      <div className="fs-8 text-muted text-truncate">{selectedTask.assigneeDept}</div>
                    )}
                  </div>
                </Col>
                <Col xs={6} sm={3}>
                  <div className="p-2 border rounded-2 bg-white text-center h-100">
                    <div className="fs-8 text-muted fw-bold text-uppercase">Start Date:</div>
                    <div className="fw-semibold text-dark fs-7 mt-1">
                      <FontAwesomeIcon icon={faCalendarAlt} className="me-1 text-primary small" />
                      {formatDisplayDate(selectedTask.startDate)}
                    </div>
                  </div>
                </Col>
                <Col xs={6} sm={3}>
                  <div className="p-2 border rounded-2 bg-white text-center h-100">
                    <div className="fs-8 text-muted fw-bold text-uppercase">Due Date:</div>
                    <div className="fw-semibold text-dark fs-7 mt-1">
                      <FontAwesomeIcon icon={faCalendarAlt} className="me-1 text-danger small" />
                      {formatDisplayDate(selectedTask.deadline || selectedTask.endDate)}
                      {selectedTask.isOverdue && selectedTask.status !== "Completed" && (
                        <span className="badge bg-danger ms-1" style={{ fontSize: "0.6rem" }}>Overdue</span>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Manager Feedback & Guidance Action Panel (Read-only status managed by employee) */}
              <div className="task-update-panel p-3 mb-4 rounded-3 border bg-white shadow-sm">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="fw-bold mb-0 d-flex align-items-center gap-2">
                    <FontAwesomeIcon icon={faUserCheck} className="text-primary" />
                    <span>Task Status & Progress</span>
                  </h6>
                  <span className="badge bg-secondary-subtle text-secondary border fs-8">
                    <FontAwesomeIcon icon={faLock} className="me-1" />
                    Employee Managed
                  </span>
                </div>

                <Row className="g-3 align-items-center mb-3">
                  <Col xs={12} sm={6}>
                    <div className="p-2 border rounded bg-light">
                      <div className="text-muted fs-8 fw-bold text-uppercase mb-1">Current Status</div>
                      <div>{renderStatusBadge(selectedTask)}</div>
                      <div className="fs-8 text-muted mt-1">Status changes are updated by {selectedTask.assigneeName}</div>
                    </div>
                  </Col>

                  <Col xs={12} sm={6}>
                    <div className="p-2 border rounded bg-light">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="text-muted fs-8 fw-bold text-uppercase">Employee Progress</span>
                        <span className="badge bg-primary fs-7">{selectedTask.progress}%</span>
                      </div>
                      <ProgressBar
                        now={selectedTask.progress}
                        variant={selectedTask.progress === 100 ? "success" : selectedTask.progress > 50 ? "info" : "warning"}
                        style={{ height: "7px" }}
                      />
                      <div className="fs-8 text-muted mt-1">Updated by {selectedTask.assigneeName}</div>
                    </div>
                  </Col>
                </Row>
{/* 
                <Form.Group className="mb-2">
                  <Form.Label className="fs-7 fw-bold">Add Manager Guidance or Review Comment</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Provide constructive review notes, unblock items, or QA verification guidance for the employee..."
                    value={managerReviewNote}
                    onChange={(e) => setManagerReviewNote(e.target.value)}
                  />
                </Form.Group> */}

                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    {feedbackSuccessAlert && (
                      <span className="text-success fw-bold fs-7">
                        <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                        Manager guidance note posted to employee!
                      </span>
                    )}
                  </div>
                  {/* <Button
                    variant="primary"
                    size="sm"
                    className="fw-bold px-3 d-flex align-items-center gap-2"
                    onClick={handleManagerSaveFeedback}
                    disabled={!managerReviewNote.trim()}
                  >
                    <FontAwesomeIcon icon={faPaperPlane} />
                    <span>Post Guidance Note</span>
                  </Button> */}
                </div>
              </div>

              {/* Work Notes Audit History */}
              <div className="task-notes-history">
                <h6 className="fw-bold mb-2 d-flex align-items-center gap-2">
                  <FontAwesomeIcon icon={faHistory} className="text-secondary" />
                  <span>Work Notes & Activity Audit ({selectedTask.workNotes ? selectedTask.workNotes.length : 0})</span>
                </h6>

                {(!selectedTask.workNotes || selectedTask.workNotes.length === 0) ? (
                  <div className="text-muted fs-7 py-3 text-center border rounded bg-white">
                    No work notes logged on this task yet.
                  </div>
                ) : (
                  <div className="work-notes-timeline">
                    {[...selectedTask.workNotes].reverse().map((note) => (
                      <div key={note.id} className="timeline-note-item">
                        <div className="timeline-note-badge">
                          <FontAwesomeIcon icon={faCommentAlt} />
                        </div>
                        <div className="timeline-note-body">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="fw-bold text-dark fs-7">
                              {note.author} <span className="text-muted fw-normal">({note.authorRole})</span>
                            </span>
                            <span className="text-muted fs-8">
                              {formatDisplayDate(note.timestamp)} {new Date(note.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            </span>
                          </div>
                          <p className="mb-1 text-dark fs-7">{note.text}</p>
                          <div className="d-flex gap-2 fs-8 text-muted">
                            {note.statusSnapshot && (
                              <span className="badge bg-light text-dark border">
                                Status: {note.statusSnapshot}
                              </span>
                            )}
                            {note.progressSnapshot !== undefined && (
                              <span className="badge bg-light text-dark border">
                                Progress: {note.progressSnapshot}%
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Modal.Body>
           
          </>
        )}
      </Modal>
    </div>
  );
};

export default ManagerTaskManagement;
